import { useEffect, useCallback } from 'react';
import { getOutgoers } from 'react-flow-renderer';
import { keypressMove, keypressShiftLeft, keypressShiftRight } from '../mapUtils/dragAndDrop';
import { getSelectedNode, getParent, getFirstChild, getNodesWithPosition } from "../mapUtils/mapFunctions";
import { defaultNodeHeight } from '../mapUtils/mapConstants';
import { handleSetMoveSource } from '../mapUtils/dragAndDrop';
import { useStore } from './store';
import { announce } from '../components/subcomponents/DescribedByTargets';
/*

m           - select main contention
r           - add reason node
o           - add objection
c           - add copremise
d           - delete node
f           - select move source
t           - execute move
b           - move copremise left
n           - move copremise right
s           - open scratch pad
upArrow     - parent
downArrow   - first child
rightArrow  - next sibling
leftArrow   - previous sibling

*/

export function useKeypress({map, setShouldLayout, deleteCurrentNode, addChildNode, toggleCurrentNode, countNodes}) {

    const { nodes, setNodes, edges, setEdges, name, setName, mapId, setMapId, undo, redo, moveSource, setMoveSource, setShouldCache, setNotesOpen, notesOpen } = map

    const onKeyPress = useCallback(e=>{
        // console.log(e.code, e.shiftKey, e.altKey)
        if(e.metaKey && (e.code == "KeyZ")){
            e.preventDefault()
            e.stopPropagation()
            e.shiftKey ? redo() : undo()
        }
        if(e.ctrlKey && (e.code == "KeyZ")){
            e.preventDefault()
            e.stopPropagation()
            undo()
        }
        if(e.ctrlKey && (e.code == "KeyY")){
            e.preventDefault()
            e.stopPropagation()
            redo()
        }
        // if(e.code == "Backspace"){
        //     e.preventDefault()
        //     deleteCurrentNode()
        // }
        if(!e.shiftKey || !e.altKey) return;
        switch(e.code){
            case "Enter":{
                e.preventDefault()
                // console.log("enter")
                const selected = getSelectedNode(nodes)
                // check if it exists and is a textInput
                if(!selected || !selected.data || !selected.data.textInputRef || !selected.data.textInputRef.current) return;
                const textInput = selected.data.textInputRef.current
                if(textInput === document.activeElement){
                    textInput.blur()
                } else {
                    textInput.focus()
                }
                break;
            }
            case "Escape":{
                e.preventDefault()
                console.log("clear drag source")
                setMoveSource(undefined)
                handleSetMoveSource(undefined, edges, setNodes)
                setShouldLayout(true)
                break;
            }
            case "ArrowUp":{
                e.preventDefault()
                // console.log("up")
                const newNodes = [...nodes]
                const selected = getSelectedNode(newNodes)
                if(!selected) return;
                const parent = getParent( selected, newNodes, edges )
                if(!parent) return;
                parent.selected = true
                selected.selected = false
                setNodes(newNodes)
                setShouldLayout(true)
                if(parent.data.ref && parent.data.ref.current){
                    parent.data.ref.current.focus()
                }
                break;
            }
            case "ArrowDown":{
                e.preventDefault()
                // console.log("down")
                const newNodes = [...nodes]
                const selected = getSelectedNode(newNodes)
                if(!selected) return;
                const child = getFirstChild(selected, newNodes, edges)
                if(!child) return;
                child.selected = true
                selected.selected = false
                setNodes(newNodes)
                setShouldLayout(true)
                if(child.data.ref && child.data.ref.current){
                    child.data.ref.current.focus()
                }
                break;
            }
            case "ArrowLeft":{
                e.preventDefault()
                const newNodes = [...nodes]
                const selected = getSelectedNode(newNodes)
                const rank = selected.data.rank
                const siblingsRaw = nodes.filter(x=>x.data.rank == rank)
                const siblings = getNodesWithPosition(siblingsRaw)
                const sortedSiblings = siblings.sort((a,b)=>a.position.x - b.position.x)
                const indexOfSelected = sortedSiblings.findIndex(n=>n.id == selected.id)
                if(indexOfSelected == 0)return(console.log("its the first sibling"));
                const prevId = sortedSiblings[indexOfSelected - 1].id
                const prev = newNodes.filter(n=>n.id == prevId)[0]
                prev.selected = true
                selected.selected = false
                if(prev.data.ref && prev.data.ref.current){
                    prev.data.ref.current.focus()
                }
                setNodes(newNodes)
                break;
            }
            case "ArrowRight":{
                e.preventDefault()
                const newNodes = [...nodes]
                // console.log("right")
                const selected = getSelectedNode(newNodes)
                const rank = selected.data.rank
                const siblingsRaw = newNodes.filter(x=>x.data.rank == rank)
                const siblings = getNodesWithPosition(siblingsRaw)
                const sortedSiblings = siblings.sort((a,b)=>a.position.x - b.position.x)
                const indexOfSelected = sortedSiblings.findIndex(n=>n.id == selected.id)
                if(indexOfSelected >= (sortedSiblings.length - 1))return(console.log("its the last sibling"));
                const nextId = sortedSiblings[indexOfSelected + 1].id
                const next = newNodes.filter(n=>n.id == nextId)[0]
                next.selected = true
                selected.selected = false
                if(next.data.ref && next.data.ref.current){
                    next.data.ref.current.focus()
                }
                setNodes(newNodes)
                break;
            }
            case "KeyD":{ // delete node
                e.preventDefault()
                // console.log("d")
                deleteCurrentNode()
                break;
            }
            case "KeyM":{ // select main contention
                e.preventDefault()
                // console.log("r")
                const node = nodes.find(n=>n.data.argType == "main-contention")
                const selected = getSelectedNode(nodes)
                if(selected)selected.selected = false
                if(node)node.selected = true
                if(node.data.ref && node.data.ref.current){
                    node.data.ref.current.focus()
                }
                break;        
            }
            case "KeyR":{ // add reason
                e.preventDefault()
                // console.log("A")
                addChildNode("editableText", "reason")
                break;
            }
            case "KeyO":{ // add objection
                e.preventDefault()
                // console.log("A")
                addChildNode("editableText", "objection")
                break;
            }
            case "KeyC":{ // add copremise
                e.preventDefault()
                // console.log("A")
                addChildNode("editableText", "copremise")
                break;
            }
            case "KeyF":{ // select move source
                e.preventDefault()
                // console.log("A")
                const source = getSelectedNode(nodes)
                setMoveSource(source)
                useStore.setState(s=>({moving: true}))
                handleSetMoveSource(source, edges, setNodes)
                setShouldLayout(true)
                console.log("select source and rerender")
                if(source && source.draggable){
                    announce("you are now dragging")
                } else {
                    announce("you cannot drag this")
                }
                // setNodes([...nodes])
                break;
            }
            case "KeyE":{ // execute move
                e.preventDefault()
                const target = getSelectedNode(nodes)
                const didMove = keypressMove({nodes, edges, moveSource, target, setNodes, setEdges, setShouldCache, setShouldLayout})
                if(didMove){
                    setMoveSource(undefined)
                    handleSetMoveSource(undefined, edges, setNodes)
                    useStore.setState(s=>({moving: false}))
                    console.log("select target")
                }
                break
            }
            case "KeyB":{ // shift copmremise left
                e.preventDefault()
                keypressShiftLeft({nodes, edges, setNodes, setShouldCache, setShouldLayout})
                break
            }
            case "KeyN":{ // shift copremise right
                e.preventDefault()
                keypressShiftRight({nodes, edges, setNodes, setShouldCache, setShouldLayout})
                break
            }
            case "KeyS":{ // shift copremise right
                e.preventDefault()
                setNotesOpen(!notesOpen)
                break
            }
            case "KeyG":{ // shift copremise right
                e.preventDefault()
                toggleCurrentNode()
                break
            }
            case "KeyP":{ // shift copremise right
                e.preventDefault()
                countNodes()
                break
            }            
        }
    },[map, setShouldLayout, deleteCurrentNode, addChildNode, nodes, setNodes, edges, setEdges, name, setName, mapId, setMapId])

    useEffect(()=>{
        document.addEventListener("keydown", onKeyPress)
        return ()=>document.removeEventListener("keydown", onKeyPress)
    },[map, setShouldLayout, deleteCurrentNode, addChildNode, nodes, setNodes, edges, setEdges, name, setName, mapId, setMapId])
}